/* eslint-disable react/jsx-props-no-spreading */
import { AuthProvider } from '@folklore/auth';
import { RoutesProvider, createPathToRegexpParser } from '@folklore/routes';
import { SocketContainer } from '@folklore/socket';
import { TrackingContainer } from '@folklore/tracking';
import { useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { Router } from 'wouter';

import { useAuthCheck } from '../hooks/useAuth';
import Tracking from '../lib/Tracking';

import { ApiContextProvider } from '../contexts/ApiContext';
import { ContestProvider } from '../contexts/ContestContext';
import { ModalsProvider } from '../contexts/ModalsContext';
import { SiteProvider } from '../contexts/SiteContext';
import App from './App';

const pathToRegexpParser = createPathToRegexpParser();

const IntlDefaultRichTextElements = {
    br: <br />,
    hr: <hr />,
    nb: (...chunks) => (
        <span
            style={{
                whiteSpace: 'nowrap',
            }}
        >
            {chunks}
        </span>
    ),
    p: (...chunks) => <p>{chunks}</p>,
    i: (...chunks) => <em>{chunks}</em>,
    em: (...chunks) => <em>{chunks}</em>,
    pem: (...chunks) => (
        <p>
            <em>{chunks}</em>
        </p>
    ),
    b: (...chunks) => <strong>{chunks}</strong>,
    strong: (...chunks) => <strong>{chunks}</strong>,
    small: (...chunks) => <small>{chunks}</small>,
    sup: (...chunks) => <sup>{chunks}</sup>,
    sub: (...chunks) => <sub>{chunks}</sub>,
    ul: (...chunks) => <ul>{chunks}</ul>,
    ol: (...chunks) => <ol>{chunks}</ol>,
    li: (...chunks) => <li>{chunks}</li>,
    h1: (...chunks) => <h1>{chunks}</h1>,
    h2: (...chunks) => <h2>{chunks}</h2>,
    h3: (...chunks) => <h3>{chunks}</h3>,
    h4: (...chunks) => <h4>{chunks}</h4>,
    h5: (...chunks) => <h5>{chunks}</h5>,
    h6: (...chunks) => <h6>{chunks}</h6>,
    s: (...chunks) => <s>{chunks}</s>,
    nbsp: `\u00a0`,
};

interface ContainerProps {
    intl: {
        locale: string;
        locales: string[];
        messages: Record<string, Record<string, string> | string>;
    };
    socket: {
        subscribeKey: string;
        namespace: string;
    };
    contest: Contest;
    routes: Record<string, string>;
    page: Page;
}

function Container({ intl, routes, contest, socket = null }: ContainerProps) {
    const { locale = 'fr', locales, messages = {} } = intl || {};
    const tracking = useMemo(() => new Tracking(), []);
    const { user } = useAuthCheck();

    return (
        <IntlProvider
            locale={locale}
            messages={messages[locale] || messages}
            defaultRichTextElements={IntlDefaultRichTextElements}
        >
            <Router parser={pathToRegexpParser}>
                <RoutesProvider routes={routes}>
                    <ApiContextProvider>
                        <SocketContainer {...socket}>
                            <AuthProvider user={user}>
                                <SiteProvider locales={locales}>
                                    <ContestProvider contest={contest}>
                                        <TrackingContainer tracking={tracking}>
                                            <ModalsProvider>
                                                <App />
                                            </ModalsProvider>
                                        </TrackingContainer>
                                    </ContestProvider>
                                </SiteProvider>
                            </AuthProvider>
                        </SocketContainer>
                    </ApiContextProvider>
                </RoutesProvider>
            </Router>
        </IntlProvider>
    );
}

export default Container;
